import { Command } from "cmdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Smartphone, ListChecks, History, Moon, Sun, Monitor, ArrowRight } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { NAV_GROUPS } from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { ApiError, get, qs } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S } from "@/lib/strings";
import type { DeviceSummary, Page, TaskRow } from "@/types/fleet";

const DEBOUNCE_MS = 200;

/**
 * ⌘K palette.
 *
 * rest-api.md has **no** `/api/v1/search` endpoint, so the async groups are
 * assembled from what the documented API does offer:
 *
 *   - devices: `GET /api/v1/devices?q=` — a real server-side search, documented;
 *   - tasks:   `/api/v1/tasks` takes no `q`, so the (small) task list is fetched
 *              once and filtered in the browser;
 *   - runs:    nothing text-searchable exists. A pasted `run_…` id is offered as
 *              a direct jump, which is the case that actually comes up.
 *
 * Flagged in the handover: if a `/search` endpoint lands, the first two groups
 * collapse into it.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  // The palette always opens empty, so the reset is unconditional and lives in
  // the event handler rather than an effect watching `open` — one render
  // instead of a render plus a corrective one.
  const resetTerm = useCallback(() => {
    setTerm("");
    setDebounced("");
  }, []);

  const setOpenAndReset = useCallback(
    (v: boolean) => {
      setOpen(v);
      resetTerm();
    },
    [resetTerm],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        resetTerm();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [resetTerm]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [term]);

  const enabled = open && debounced.length >= 2;

  const devices = useQuery<Page<DeviceSummary>, ApiError>({
    queryKey: ["palette", "devices", debounced],
    queryFn: () => get<Page<DeviceSummary>>(`/devices${qs({ q: debounced, limit: 6 })}`),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  // The whole task list, once. There are tens of tasks, not thousands, and the
  // endpoint has no q parameter to push the filter down to.
  const tasks = useQuery<Page<TaskRow>, ApiError>({
    queryKey: qk.tasks(),
    queryFn: () => get<Page<TaskRow>>("/tasks"),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });

  const taskMatches = useMemo(() => {
    if (!debounced) return [];
    const needle = debounced.toLowerCase();
    return (tasks.data?.items ?? [])
      .filter((t) => t.name.toLowerCase().includes(needle) || t.goal.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [tasks.data, debounced]);

  const runIdMatch = /^run_[a-z0-9]+$/i.test(debounced) ? debounced : null;

  function run(fn: () => void) {
    fn();
    setOpenAndReset(false);
  }

  return (
    <>
      <Command.Dialog
        open={open}
        onOpenChange={setOpenAndReset}
        label={S.commandMenu}
        shouldFilter={false}
        className="fixed inset-0 z-50 grid place-items-start bg-black/60 px-4 pt-[18vh] backdrop-blur-sm"
      >
        <Command className="mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] shadow-2xl">
          <Command.Input
            value={term}
            onValueChange={setTerm}
            placeholder="搜尋裝置、任務、run，或輸入指令…"
            className="w-full border-b border-[var(--color-border-subtle)] bg-transparent px-4 py-3 text-sm placeholder:text-[var(--color-fg-subtle)] focus:outline-none"
          />
          <Command.List className="max-h-[22rem] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-[var(--color-fg-subtle)]">
              {debounced.length < 2 ? "輸入至少 2 個字開始搜尋。" : "沒有結果。"}
            </Command.Empty>

            {runIdMatch && (
              <Group heading="Run">
                <Item onSelect={() => run(() => navigate(`/runs/${runIdMatch}`))} icon={History}>
                  <span className="font-mono">{runIdMatch}</span>
                  <Trailing>開啟 run</Trailing>
                </Item>
              </Group>
            )}

            {enabled && (devices.isFetching || (devices.data?.items.length ?? 0) > 0) && (
              <Group heading={S.navDevices}>
                {devices.isFetching && !devices.data && <Pending />}
                {devices.data?.items.map((d) => (
                  <Item
                    key={d.id}
                    onSelect={() => run(() => navigate(`/devices/${d.id}`))}
                    icon={Smartphone}
                  >
                    <span className="truncate">{d.label}</span>
                    <span className="ml-2 truncate font-mono text-[11px] text-[var(--color-fg-faint)]">
                      {d.id}
                    </span>
                    <Trailing>{d.health}</Trailing>
                  </Item>
                ))}
              </Group>
            )}

            {taskMatches.length > 0 && (
              <Group heading={S.navTasks}>
                {taskMatches.map((t) => (
                  <Item
                    key={t.id}
                    onSelect={() => run(() => navigate(`/tasks/${t.id}`))}
                    icon={ListChecks}
                  >
                    <span className="truncate">{t.name}</span>
                    <Trailing>{t.enabled ? S.enabled : S.disabled}</Trailing>
                  </Item>
                ))}
              </Group>
            )}

            <Group heading="前往">
              {NAV_GROUPS.flatMap((g) => g.items).map((n) => (
                <Item key={n.to} onSelect={() => run(() => navigate(n.to))} icon={n.icon}>
                  {n.label}
                </Item>
              ))}
            </Group>

            <Group heading="外觀">
              <Item onSelect={() => run(() => setTheme("dark"))} icon={Moon}>
                深色主題
              </Item>
              <Item onSelect={() => run(() => setTheme("light"))} icon={Sun}>
                淺色主題
              </Item>
              <Item onSelect={() => run(() => setTheme("system"))} icon={Monitor}>
                跟隨系統
              </Item>
            </Group>
          </Command.List>
        </Command>
      </Command.Dialog>

      <div className="fixed bottom-3 right-3 hidden items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1 font-mono text-[10px] text-[var(--color-fg-faint)] lg:flex">
        <kbd className="rounded bg-[rgba(255,255,255,0.05)] px-1">⌘</kbd>
        <kbd className="rounded bg-[rgba(255,255,255,0.05)] px-1">K</kbd>
        <span>{S.commandMenu}</span>
      </div>
    </>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-fg-faint)]"
    >
      {children}
    </Command.Group>
  );
}

function Item({
  onSelect,
  icon: Icon,
  children,
}: {
  onSelect: () => void;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--color-fg-muted)]",
        "aria-selected:bg-[rgba(255,255,255,0.06)] aria-selected:text-[var(--color-fg)]",
      )}
    >
      {Icon && <Icon size={14} className="shrink-0 opacity-70" />}
      {children}
    </Command.Item>
  );
}

function Trailing({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto flex items-center gap-1 pl-3 text-[11px] text-[var(--color-fg-faint)]">
      {children}
      <ArrowRight size={11} />
    </span>
  );
}

function Pending() {
  return (
    <div className="px-3 py-2 text-[11px] text-[var(--color-fg-faint)]">{S.loading}</div>
  );
}
